<script setup lang="ts">
import { CrmSchema } from "~/schemas/crm";

const { data: page } = await useAsyncData(() => 
  queryCollection('CRMPage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(CrmSchema)
    },
  ],
});

const modalsStore = useModalsStore();
const heroTabsContainer = useTemplateRef('heroTabsContainer');
const heroTabsParent = useTemplateRef('heroTabsParent');
const heroTabs = useTemplateRef('heroTabs');
const heroTabsIndicator = useTemplateRef('heroTabsIndicator');
const heroActiveTabIndex = ref(0);
const device = useDevice();

function scrollIntoViewActiveTab(elem: HTMLHeadingElement | undefined) {
  if (!elem || !heroTabs.value || !heroTabsContainer.value || device.isDesktop.value) return;

  const containerRect = heroTabsContainer.value.getBoundingClientRect();
  const elemRect = elem.getBoundingClientRect();
  const offset = elemRect.left - containerRect.left - containerRect.width / 2 + elemRect.width / 2;
  heroTabsContainer.value?.scrollBy({ left: offset });
};

function activeHeroTab(index: number) {
  if (!heroTabs.value) return;

  heroActiveTabIndex.value = index;
  moveActiveIndicator(heroTabsParent, heroTabsIndicator, heroTabs, index);
  scrollIntoViewActiveTab(heroTabs.value[index]);
};
function handleOnClickHeroTab(index: number) {
  activeHeroTab(index);
};

function handleOpenModal() {
  modalsStore.openModal('CTA');
};

function handleOnClickViewPrices() {
  const elem = document.getElementById('prices');
  elem?.scrollIntoView({ behavior: 'smooth' });
};

onMounted(async () => {
  await nextTick();
  activeHeroTab(0);
});
</script>

<template>
  <template v-if="page">
    <SectionsBase 
      class="bg-[url('/svg/new/backgrounds/mesh-05-mobile.svg')] lg:bg-[url('/svg/new/backgrounds/mesh-05.svg')] 
        bg-size-[100%_auto] bg-no-repeat lg:bg-position-[center_-250px] pt-25! lg:pt-26!"
    >
      <div class="container flex flex-col items-center">
        <div>
          <h1 class="text-primary-600 text-center highlight">{{ page.title }}</h1>
          <span class="h1"><span class="semicolon">؛</span> {{ page.subTitle }}</span>
        </div>
        <p class="text-gray-500 text-body1-semibold mt-8 text-center">{{ page.description }}</p>
        <div class="w-full flex-center flex-col lg:flex-row gap-4 mt-8 lg:mt-12">
          <Button
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            pill
            @click="handleOpenModal"
            class="relative w-46.5"
          >
            <span>رایگان امتحان کنید</span>
            <IconsCurvedArrow 
              class="w-30 text-secondary-600 absolute scale-y-[-1] origin-bottom bottom-20 -right-18 -rotate-30 hidden
                lg:block pointer-events-none" 
            />
          </Button>
          <Button
            type="Outline"
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            pill
            @click="handleOnClickViewPrices"
            class="w-46.5 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
          >
            مشاهده تعرفه‌ها
          </Button>
        </div>
        <div class="w-full mt-8 lg:mt-18">
          <ClientOnly>
            <template v-if="!device.isDesktop.value">
                <div 
                  ref="heroTabsContainer"
                  class="w-full overflow-y-hidden no-scrollbar h-14.5 flex items-center px-4 py-3 border-[5px]
                    border-primary-50 shadow-gray-s rounded-full bg-white relative"
                >
                    <div ref="heroTabsParent" class="w-max relative top-0 right-0 flex items-center">
                      <svg 
                        ref="heroTabsIndicator" 
                        class="absolute h-full bg-primary-50 transition-transform duration-500 rounded-full right-0 
                          border border-primary-100"
                      ></svg>
                      <div
                        v-for="item, index in page.items"
                        :key="item.title"
                        ref="heroTabs"
                        class="flex items-center px-3 py-1 text-gray-500 gap-2 cursor-pointer select-none relative z-1 w-max"
                        :class="heroActiveTabIndex === index && 'text-primary-600'"
                        @click="handleOnClickHeroTab(index)"
                      >
                        <Icon :name="item.icon" size="24" class="w-6" />
                        <span class="text-body1-semibold">{{ item.title }}</span>
                      </div>
                    </div>
                </div>
            </template>
            <template v-else>
              <div 
                ref="heroTabsParent"
                class="w-full flex items-center justify-between px-4 py-3 border-[5px] border-primary-50 shadow-gray-s 
                  rounded-full bg-white relative"
              >
                <svg 
                  ref="heroTabsIndicator" 
                  class="absolute h-[calc(100%-16px)] bg-primary-50 transition-transform duration-500 rounded-full right-0 
                    border border-primary-100"
                ></svg>
                <div
                  v-for="item, index in page.items"
                  :key="item.title"
                  ref="heroTabs"
                  class="flex items-center px-3 py-1 text-gray-400 gap-2 cursor-pointer select-none relative z-1"
                  :class="heroActiveTabIndex === index && 'text-primary-600'"
                  @click="handleOnClickHeroTab(index)"
                >
                  <Icon :name="item.icon" size="24" class="w-6" />
                  <span class="text-body2" :class="heroActiveTabIndex === index && 'font-semibold!'">{{ item.title }}</span>
                </div>
              </div>
            </template>
          </ClientOnly>
          <div class="w-full mt-7 min-h-50 lg:h-125">
            <Transition name="fade" mode="out-in">
              <img
                :key="heroActiveTabIndex"
                :src="page.items[heroActiveTabIndex]?.img"
                :alt="page.items[heroActiveTabIndex]?.title"
                class="w-full lg:w-auto lg:h-125 mx-auto"
              />
            </Transition>
          </div>
        </div>
      </div>
    </SectionsBase>

    <LazyIdleSectionsLogos hydrate-on-idle />

    <LazyVisibleTOC hydrate-on-visible :topics="page.topics" />

    <LazySectionsSolutionsCrm
      :title="page.solutions.title"
      :desc="page.solutions.desc"
      :items="page.solutions.items"
    />

    <LazySectionsFeaturesCrm
      :title="page.features.title"
      :desc="page.features.desc"
      :items="page.features.items"
    />

    <LazyIdleSectionsFacilitiesCrm
      hydrate-on-idle
      :title="page.facilities.title"
      :desc="page.facilities.desc"
    />

    <LazyIdleSectionsNavatelOverview
      hydrate-on-idle
      :title="page.navatelOverview.title"
      :subTitle="page.navatelOverview.subTitle"
      :desc="page.navatelOverview.desc"
    />

    <LazyIdleSectionsRelatedProductsShowcase
      hydrate-on-idle
      :title="page.relatedProducts.title"
      :desc="page.relatedProducts.desc"
      :items="page.relatedProducts.items"
    />

    <LazyIdleSectionsWhyNavatel
      hydrate-on-idle
      :title="page.whyNavatel.title"
      :desc="page.whyNavatel.desc"
      :items="page.whyNavatel.items"
    />

    <LazyVisibleSectionsPricesCrm
      hydrate-on-visible
      :title="page.prices.title"
      :desc="page.prices.desc"
    />

    <LazyVisibleSectionsComments hydrate-on-visible />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />

    <LazyVisibleSectionsCTA 
      hydrate-on-visible 
      :title="page.CTA.title"
    />
  </template>
</template>